import { useLazyQuery } from '@apollo/client'

import { PinPurpose } from '@flowwai-frontend/bps-officer-web/graphql'
import {
   convertToErrorType,
   CustomError,
   getAPIStatusFromNetworkStatus
} from '@flowwai-frontend/shared-web/common/data-access'
import { UserPIN } from '@flowwai-frontend/shared-web/iam/data-access'
import { APIStatus } from '@flowwai-frontend/shared/api-common'

import {
   GetUserPinStatusQuery as Query,
   GetUserPinStatusDocument as QueryDocument,
   GetUserPinStatusQueryVariables as QueryVariables
} from './query.generatedTypes'
import useResponseHandler from './responseHandler'

export interface FailureArgs {
   data?: CustomError<Query['getUserPinStatus']>
   error?: Error
}

interface TriggerAPIArgs {
   onSuccess?: (userPIN: UserPIN) => void
   onFailure?: (args: FailureArgs) => void
}

interface ReturnType {
   apiError: Error | null
   apiStatus: APIStatus
   triggerAPI: (args: TriggerAPIArgs) => void
}

const GetUserPinStatus = (): ReturnType => {
   const [getUserPinStatus, { data, error, networkStatus }] = useLazyQuery<
      Query,
      QueryVariables
   >(QueryDocument)

   const { handleAPIResponse } = useResponseHandler()

   const triggerAPI = (triggerArgs: TriggerAPIArgs): void => {
      const handleAPIFailure = (
         errorMessage: string,
         data: Query['getUserPinStatus']
      ) =>
         triggerArgs.onFailure?.({
            data: new CustomError(errorMessage, data)
         })

      getUserPinStatus({
         variables: {
            params: {
               pinPurpose: PinPurpose.ESign
            }
         },
         fetchPolicy: 'network-only',

         onCompleted: (data): void => {
            handleAPIResponse({
               data,
               onSuccess: triggerArgs.onSuccess,
               onFailure: handleAPIFailure
            })
         },
         onError: (error): void => {
            triggerArgs.onFailure?.({ error })
         }
      })
   }

   const apiError = convertToErrorType(error)
   const apiStatus = getAPIStatusFromNetworkStatus(networkStatus, data)

   return {
      apiError,
      apiStatus,
      triggerAPI
   }
}

export default GetUserPinStatus
